import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {Store} from '@softline/core';
import {UntypedFormControl, UntypedFormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, Subject} from 'rxjs';
import {FocusMonitor} from '@angular/cdk/a11y';
import {map} from 'rxjs/operators';
import {
  HardwareManagementProdukteStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_PRODKUTE
} from '../../../hardware-management.shared';
import {Produkt} from '../../../types/produkt';
import {showRequestErrors} from '@softline/application';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';
import {HerstellerInputComponent} from '../hersteller-input/hersteller-input.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    // tslint:disable-next-line:component-selector
    selector: 'lib-produkt-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule, HerstellerInputComponent,
    EntityInputComponent,
    InputComponent,
    TranslatePipe],
    templateUrl: './produkt-input.component.html',
    styleUrls: ['./produkt-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProduktInputComponent {

  @Input() readonly = false;
  @Input() required = false;

  readonly productInEditing$ = new BehaviorSubject<Produkt | null>(null);

  readonly clearInput$ = new Subject<void>();

  readonly products$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_PRODKUTE,
    HardwareManagementProdukteStore.getters.all
  ).pipe(map(o => o as Produkt[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly productForm = new UntypedFormGroup({
    produkt: new UntypedFormControl(null, Validators.required()),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null),
    hersteller: new UntypedFormControl(null, Validators.required())
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(produkt: Produkt | null | undefined): void {
    if(!produkt)
      return;
    this.formGroup.patchValue({ produkt, hersteller: produkt.hersteller });
    this.clearInput$.next();
  }

  edit(entity: Produkt | null | undefined): void {
    if(!entity)
      return;
    this.productInEditing$.next(entity);
    this.productForm.patchValue(entity);
  }

  resetForm(produkt?: Produkt | null): void {
    if (!produkt) {
      this.productForm.reset();
      return;
    }

    this.productForm.reset(produkt);
  }

  async saveEdit(entity: Produkt): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.productForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_PRODKUTE,
        HardwareManagementProdukteStore.actions.update,
        { entity: updatedEntity }
      );

      this.select(result);
      this.productForm.reset();
      this.productInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Produkt wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async createProduct(entity: Partial<Produkt>): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_PRODKUTE,
        HardwareManagementProdukteStore.actions.create,
        { entity }
      );

      this.select(result);
      this.productForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Produkt wurde erfolgreich angelegt'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: Produkt | undefined | null): void {
    if (!newValue) {
      this.productForm.reset();
      this.productInEditing$.next(null);
    }

    this.clearInput$.next();

    if (newValue && this.formGroup.value?.produkt?.id !== newValue?.id) {
      this.formGroup.patchValue({ produkt: newValue }, { onlySelf: true });
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ produkt: null });
    this.productForm.reset();
    this.productInEditing$.next(null);
    this.clearInput$.next();

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.productForm.reset();
    this.clearInput$.next();
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterProducts = (products: Produkt[], filter: string): Produkt[] => {
    const herstellerValue = this.formGroup.get('hersteller')?.value;
    this.productForm.patchValue({ hersteller: herstellerValue }, { emitEvent: false, onlySelf: true });

    if (!filter || filter?.length < 1) {
      return !!herstellerValue
        ? products.filter(product => product?.hersteller?.id === herstellerValue?.id)
        : products;
    }

    return products.filter(product => {
      const result = product.produkt.toLowerCase().includes(filter)
        || product.bezeichnung.toLowerCase().includes(filter)
        || product.beschreibung.toLowerCase().includes(filter);

      return !!herstellerValue
        ? (result && product?.hersteller?.id === herstellerValue?.id)
        : result;
    });
  }
}
